import React, { useMemo, useCallback, useState } from 'react'
import { UserService } from '@duck/api-definitions'
import { useTar } from '@/modules/i18n'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { FunnelIcon } from '@heroicons/react/24/outline'
import {
  getDefaultVisibleColumns,
  createToggleColumnVisibilityWithProtectedColumn,
  renderSortIcon,
} from '@/hooks/TableSettings/generalTable.utils'
import {
  allDashboardColumns,
  type DashboardColumnDef,
  type DashboardColumnKey,
  type PartnerDashboardRow,
  getCustomStatusBadgeColor,
  formatCustomStatus,
  getCustomStatusOptions,
  sortPartnerDashboardRows,
  filterPartnerDashboardRows,
} from '@/hooks/TableSettings/partnerDashboardTable.utils'
import {
  TextFilterPopover,
  EnumFilterDropdown,
  ColumnVisibilityDropdown,
  ClearAllSettingsButton,
} from '@/hooks/TableSettings/tableSettings.components'
import { GradeBadge } from '../GradeBadge/GradeBadge'
import {
  ChallengeSummaryDialog,
  type FeedbackDetails,
} from '../PartnerCohorts/Assignments/ChallengeSummaryDialog'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { TranslationKey } from '@/modules/i18n'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'

interface PartnerDashboardTableProps {
  rows: PartnerDashboardRow[]
  persons: UserService.Person[]
}

export const PartnerDashboardTable: React.FC<PartnerDashboardTableProps> = ({
  rows,
  persons,
}) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackDetails | null>(null)

  const defaultVisibleColumns = useMemo(
    () => getDefaultVisibleColumns(allDashboardColumns),
    [],
  )

  const {
    sortColumn,
    sortDirection,
    toggleSort,
    isColumnVisible,
    toggleColumnVisibility: originalToggleColumnVisibility,
    getSortState,
    getColumnFilter,
    setColumnFilter,
    getColumnFilterArray,
    toggleColumnFilterArrayItem,
    clearColumnFilter,
    clearAllSettings,
    hasActiveSettings,
  } = useTableSettings({
    tableId: 'partnerDashboard-all',
    defaultVisibleColumns,
  })

  const toggleColumnVisibility = useMemo(
    () => createToggleColumnVisibilityWithProtectedColumn(originalToggleColumnVisibility, 'personId'),
    [originalToggleColumnVisibility],
  )

  const customStatusOptions = useMemo(() => getCustomStatusOptions(t), [t])

  const getEnumOptionsForColumn = useCallback(
    (columnKey: DashboardColumnKey) => {
      if (columnKey === 'customStatus') return customStatusOptions
      return undefined
    },
    [customStatusOptions],
  )

  const visibleColumns = useMemo(
    () => allDashboardColumns.filter((col) => col.key === 'personId' || isColumnVisible(col.key)),
    [isColumnVisible],
  )

  const sortedRows = useMemo(
    () => sortPartnerDashboardRows(rows, persons, sortColumn, sortDirection),
    [rows, persons, sortColumn, sortDirection],
  )

  const filteredAndSortedRows = useMemo(
    () =>
      filterPartnerDashboardRows(
        sortedRows,
        persons,
        allDashboardColumns,
        getColumnFilter,
        getColumnFilterArray,
      ),
    [sortedRows, persons, getColumnFilter, getColumnFilterArray],
  )

  const hasRowsButNoResults = rows.length > 0 && filteredAndSortedRows.length === 0
  const hasActiveFilters = hasActiveSettings()

  const handleGradeClick = useCallback(
    (row: PartnerDashboardRow) => {
      const person = persons.find((p) => p.id === row.personId)
      const personName = person
        ? `${person.firstName || ''} ${person.lastName || ''}`.trim()
        : row.email

      setSelectedFeedback({
        personName: personName || row.email,
        feedback: row.feedback || '',
        startDate: row.startDate,
        submitDate: row.submitDate,
        workTime: row.workTime,
        grade: row.grade,
        solution: row.solution,
        charts: row.charts,
        queries: row.queries,
        challengeId: row.challengeId,
        chosenWeapon: row.chosenWeapon,
        jNotebook: row.jNotebook,
      })
      setFeedbackDialogOpen(true)
    },
    [persons],
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">
          {tP('peopleAssignmentsOverview')}
        </h2>
        <div className="flex items-center gap-2">
          {hasActiveSettings() && <ClearAllSettingsButton onClear={clearAllSettings} />}
          <ColumnVisibilityDropdown
            availableColumns={allDashboardColumns.map((col) => {
              const label = col.partnerLabel ? tP(col.partnerLabel) : col.label ? t(col.label) : ''
              return { key: col.key, label }
            })}
            isColumnVisible={isColumnVisible}
            toggleColumnVisibility={toggleColumnVisibility}
            protectedColumnKeys={['personId']}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-border rounded-md divide-y divide-border">
          <thead className="bg-muted/15">
            <tr>
              {visibleColumns.map((col) => {
                const isEnumFilter = col.filterType === 'enum'
                const filterValue = isEnumFilter ? undefined : getColumnFilter(col.key)
                const filterArray = isEnumFilter ? getColumnFilterArray(col.key) : undefined
                const hasFilter = isEnumFilter
                  ? (filterArray?.length || 0) > 0
                  : !!filterValue?.trim()
                const enumOptions = isEnumFilter
                  ? getEnumOptionsForColumn(col.key)
                  : undefined
                const label = col.partnerLabel ? tP(col.partnerLabel) : col.label ? t(col.label) : ''

                return (
                  <th
                    key={col.key}
                    className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleSort(col.key)}
                        className="group flex items-center gap-1.5 hover:text-foreground transition-colors"
                      >
                        <span>{label}</span>
                        {renderSortIcon(getSortState(col.key))}
                      </button>
                      {isEnumFilter && enumOptions ? (
                        <EnumFilterDropdown
                          label={label as TranslationKey}
                          labelT={t}
                          enumOptions={enumOptions}
                          filterArray={filterArray || []}
                          hasFilter={hasFilter}
                          onToggleFilterItem={(value) =>
                            toggleColumnFilterArrayItem(col.key, value)
                          }
                          onClearFilter={() => clearColumnFilter(col.key)}
                        />
                      ) : (
                        <TextFilterPopover
                          label={label as TranslationKey}
                          labelT={t}
                          filterValue={filterValue || ''}
                          hasFilter={hasFilter}
                          onFilterChange={(value) => setColumnFilter(col.key, value)}
                          onClearFilter={() => clearColumnFilter(col.key)}
                        />
                      )}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {hasRowsButNoResults ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-6 py-8">
                  <NoDataEmptyState
                    icon={FunnelIcon}
                    title={t('common.noFilterResults')}
                    description={t('common.noFilterResultsDescription')}
                    buttonText={hasActiveFilters ? t('common.resetFilters') : undefined}
                    onButtonClick={hasActiveFilters ? clearAllSettings : undefined}
                  />
                </td>
              </tr>
            ) : (
              filteredAndSortedRows.map((row) => {
                const person = persons.find((p) => p.id === row.personId)

                const renderCell = (col: DashboardColumnDef): React.ReactNode => {
                  const cellRenderers: Record<DashboardColumnKey, () => React.ReactNode> = {
                    personId: () => (
                      <td key="personId" className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                              <span className="text-sm font-medium text-accent">
                                {person?.firstName?.charAt(0) || ''}
                                {person?.lastName?.charAt(0) || ''}
                              </span>
                            </div>
                          </div>
                          <div className="ms-4">
                            <p className="text-sm font-medium text-foreground">
                              {person?.firstName || ''} {person?.lastName || ''}
                            </p>
                          </div>
                        </div>
                      </td>
                    ),
                    email: () => (
                      <td key="email" className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-muted-foreground">{row.email}</p>
                      </td>
                    ),
                    taskDisplayName: () => (
                      <td key="taskDisplayName" className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-foreground">{row.taskDisplayName}</p>
                      </td>
                    ),
                    customStatus: () => (
                      <td key="customStatus" className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCustomStatusBadgeColor(row.customStatus)}`}
                        >
                          {t(formatCustomStatus(row.customStatus))}
                        </span>
                      </td>
                    ),
                    grade: () => (
                      <td key="grade" className="px-6 py-4 whitespace-nowrap">
                        {row.feedback && row.grade !== undefined ? (
                          <div
                            onClick={() => handleGradeClick(row)}
                            className="cursor-pointer"
                          >
                            <GradeBadge grade={row.grade} small />
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {t('common.BLANK')}
                          </span>
                        )}
                      </td>
                    ),
                    assignmentSentDate: () => (
                      <td key="assignmentSentDate" className="px-6 py-4 whitespace-nowrap">
                        <RelativeDateTime
                          isoDateString={row.assignmentSentDate}
                          fallbackText={t('common.BLANK')}
                          className="text-sm text-muted-foreground"
                        />
                      </td>
                    ),
                    cohortName: () => (
                      <td key="cohortName" className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-foreground">
                          {row.cohortName ?? t('common.BLANK')}
                        </p>
                      </td>
                    ),
                  }

                  return cellRenderers[col.key]()
                }

                return (
                  <tr
                    key={`${row.personId}-${row.assignmentId}`}
                    className="hover:bg-secondary/5"
                  >
                    {visibleColumns.map((col) => renderCell(col))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      <ChallengeSummaryDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        feedbackDetails={selectedFeedback}
      />
    </div>
  )
}
